import { BBDTag, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'

import {
  ManualOrGPSTag,
  MiniStepTag,
  ProjectedStepTime,
  RouteDistance,
} from '@/components/OrderDispatch/DriverTracking/components/RouteDetailView/components/RouteStepItem'
import { DriverStatusIcon } from '@/components/OrderDispatch/DriverTracking/components/DriverStatusIcon'
import { StepTimePopover } from '@/components/OrderDispatch/DriverTracking/components/RouteDetailView/components/TimePopoverContent'
import { dayjsWithTimezone, formatters, Timezone } from '@/utils/intl/dateTimeFormatters'
import { DriverSchedule, MapboxCoords } from '@/components/OrderDispatch/DriverTracking/types'
import { Empty } from 'antd'
import { IconRoadOff } from '@tabler/icons-react'

interface RouteLogTimelineProps {
  orderRoute: DriverSchedule['route'] | null
  timezone: Timezone
  flyToLocation: (location: MapboxCoords) => void
}

export function RouteLogTimeline({ orderRoute, timezone, flyToLocation }: RouteLogTimelineProps) {
  return (
    <Vertical flex={orderRoute?.length ? 1.2 : 0.75} className='px-2' gap={8} scroll>
      <Texto category='p2' className='py-2'>
        Route Timeline
      </Texto>
      {!orderRoute?.length && (
        <Vertical flex={1} verticalCenter horizontalCenter style={{ color: 'var(--gray-400)' }}>
          <IconRoadOff size={40} />
          <Texto className='mt-2' appearance='hint'>
            No route timeline available.
          </Texto>
        </Vertical>
      )}
      {orderRoute?.map((step) => (
        <Vertical
          flex={'none'}
          height={'auto'}
          key={step.action + step.destination_id}
          gap={4}
          style={{ overflow: 'visible' }}
        >
          <StepTimeBlock step={step} timezone={timezone} />
          <Horizontal alignItems='center' className='ml-4' gap={'10px'}>
            <MiniStepTag step={step} />
            <ProjectedStepTime step={step} />
            {step.route_distance ? <RouteDistance step={step} /> : null}
            <Horizontal flex={1} justifyContent='flex-end'>
              <GraviButton
                appearance='text'
                onClick={() => flyToLocation([step.destination_lon || 0, step.destination_lat || 0])}
                disabled={!step.destination_lat || !step.destination_lon}
              >
                {step.destination_site_name || step.destination_name}
              </GraviButton>
            </Horizontal>
          </Horizontal>
        </Vertical>
      ))}
    </Vertical>
  )
}

type InvoiceStepTimeBlockProps = {
  step: DriverSchedule['route'][number]
  timezone: Timezone
}

function StepTimeBlock({ step, timezone }: InvoiceStepTimeBlockProps) {
  return (
    <Horizontal verticalCenter gap={4}>
      <DriverStatusIcon action={step?.action} size='medium' />
      <Texto category='h5' className='flex-1' weight={'bold'}>
        {dayjsWithTimezone(step.start_time || step.projected_start_time, timezone).format('h:mm A')} —{' '}
        {formatters.TIME(step.end_time || step.projected_end_time, timezone)}
      </Texto>
      {step.complete && (
        <>
          <StepTimePopover step={step} timezone={timezone} />
          <ManualOrGPSTag step={step} />
        </>
      )}
      {step.current && (
        <BBDTag theme2 className='route-step-current-tag ml-2'>
          In Progress
        </BBDTag>
      )}
    </Horizontal>
  )
}
